import { withoutListingPages } from "@/lib/listingPageUrl";

const NUMBERED = /^(\d+)\.\s/;

/**
 * D9 / J4 — this listing's own entry in a combined intro that went out, or null.
 *
 * <p>The entry is found by its NUMBER, never by its title: the server stores this
 * listing's position (`latestSendCombinedPosition`, from the combined email's
 * ordered ids) and how many listings the email covered. The body itself was
 * editable before it went, so the number is trusted only when the sent body's
 * numbered lines are still exactly `1.` … `N.`, in order, once the locked
 * listing-pages section (which numbers its own lines) is taken off. Anything
 * else — a number edited, deleted, duplicated or out of order, or a section that
 * no longer matches — returns null, and the card falls back to the summary line
 * and the full email.</p>
 *
 * <p>The entry runs from line `{pos}.` to the line before `{pos + 1}.`; the last
 * entry runs to the end of its block (the blank line before the closing).</p>
 */
export function combinedEntry(
  sentBody: string | null | undefined,
  listingPagesSection: string | null | undefined,
  position: number | null | undefined,
  count: number,
): string | null {
  if (!sentBody || position == null || !Number.isInteger(position) || position < 1 || position > count) {
    return null;
  }

  const lines = withoutListingPages(sentBody, listingPagesSection).split("\n");
  const numbered: { line: number; n: number }[] = [];
  lines.forEach((text, line) => {
    const m = NUMBERED.exec(text);
    if (m) numbered.push({ line, n: Number(m[1]) });
  });

  if (numbered.length !== count || numbered.some((x, i) => x.n !== i + 1)) return null;

  const start = numbered[position - 1].line;
  let end: number;
  if (position < count) {
    end = numbered[position].line;
  } else {
    const blank = lines.findIndex((text, i) => i > start && text.trim() === "");
    end = blank < 0 ? lines.length : blank;
  }
  const entry = lines.slice(start, end).join("\n").trim();
  return entry.length > 0 ? entry : null;
}
